import { cloneDeep } from 'lodash';
import apiClient from '@/utils/apiClient';

const DRAFTS_ENDPOINT = '/api/price-list-drafts/';

/**
 * @param {string} priceListType
 * @param {number|string|null} priceListId
 * @returns {{ type: string, price_list_id: (number|string) }}
 */
function draftScopeParams(priceListType, priceListId) {
    return {
        type: priceListType || 'general',
        price_list_id: priceListId || 'none',
    };
}

/**
 * Loads all product drafts for a price list scope from the backend.
 * @param {string} priceListType
 * @param {number|string|null} priceListId
 * @returns {Promise<Array<Object>>}
 */
export async function fetchDraftQueue(priceListType, priceListId) {
    try {
        const response = await apiClient.get(DRAFTS_ENDPOINT, {
            params: draftScopeParams(priceListType, priceListId),
        });
        return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
        console.error('Failed to load price list drafts:', error);
        return [];
    }
}

/**
 * @param {unknown} id
 * @returns {boolean}
 */
export function isPersistedDraftId(id) {
    if (id == null || id === '') {
        return false;
    }
    if (typeof id === 'number') {
        return Number.isInteger(id) && id > 0;
    }
    const normalized = String(id).trim();
    return /^\d+$/.test(normalized);
}

/**
 * @param {unknown} id
 * @returns {number|null}
 */
export function normalizePersistedDraftId(id) {
    if (!isPersistedDraftId(id)) {
        return null;
    }
    return typeof id === 'number' ? id : Number(String(id).trim());
}

/**
 * Creates or updates a single draft entry on the backend.
 * @param {Object} params
 * @param {string} params.priceListType
 * @param {number|string|null} params.priceListId
 * @param {Object} params.entry - Draft entry (see createDraftEntry).
 * @returns {Promise<Object>} The persisted draft entry (with server id).
 */
export async function saveDraftEntry({ priceListType, priceListId, entry }) {
    const body = {
        ...draftScopeParams(priceListType, priceListId),
        action: entry.action,
        updateMode: entry.updateMode,
        payload: entry.payload,
        builderState: entry.builderState,
        summary: entry.summary,
        sourceProductCode: entry.sourceProductCode,
        previousPrice: entry.previousPrice,
    };
    const persistedId = normalizePersistedDraftId(entry?.id);
    if (persistedId != null) {
        body.id = persistedId;
    }
    const response = await apiClient.post(DRAFTS_ENDPOINT, body);
    return response.data;
}

/**
 * Resolves the backend draft id for a queue entry (handles client UUID ids).
 * @param {Object} draft
 * @param {string} priceListType
 * @param {number|string|null} priceListId
 * @returns {Promise<number|null>}
 */
export async function resolvePersistedDraftId(draft, priceListType, priceListId) {
    const direct = normalizePersistedDraftId(draft?.id);
    if (direct != null) {
        return direct;
    }
    const productCode = String(draft.payload?.product_code || '').trim();
    if (!productCode) {
        return null;
    }
    const drafts = await fetchDraftQueue(priceListType, priceListId);
    const match = drafts.find(
        (row) => String(row.payload?.product_code || '').trim() === productCode,
    );
    return normalizePersistedDraftId(match?.id);
}

/**
 * Moves a draft from one price list scope to another.
 * @param {Object} params
 * @param {string} params.priceListType
 * @param {number|string|null} [params.fromPriceListId]
 * @param {number|string} params.toPriceListId
 * @param {Object} params.entry
 * @returns {Promise<Object>}
 */
export async function migrateDraftEntry({
    priceListType,
    fromPriceListId = null,
    toPriceListId,
    entry,
}) {
    const persistedId = normalizePersistedDraftId(entry?.id);
    if (persistedId != null) {
        return saveDraftEntry({
            priceListType,
            priceListId: toPriceListId,
            entry: { ...entry, id: persistedId },
        });
    }

    const productCode = String(entry.payload?.product_code || '').trim();
    const created = await saveDraftEntry({
        priceListType,
        priceListId: toPriceListId,
        entry,
    });

    if (productCode && fromPriceListId != null && fromPriceListId !== '') {
        const oldDrafts = await fetchDraftQueue(priceListType, fromPriceListId);
        const oldMatch = oldDrafts.find(
            (draft) => String(draft.payload?.product_code || '').trim() === productCode,
        );
        const oldId = normalizePersistedDraftId(oldMatch?.id);
        if (oldId != null) {
            try {
                await deleteDraftEntry(oldId);
            } catch (error) {
                console.error(
                    'Failed to remove migrated draft from previous scope:',
                    error,
                );
            }
        }
    }

    return created;
}

/**
 * Deletes a single draft entry from the backend.
 * @param {number|string} draftId
 * @returns {Promise<void>}
 */
export async function deleteDraftEntry(draftId) {
    const persistedId = normalizePersistedDraftId(draftId);
    if (persistedId == null) {
        return;
    }
    await apiClient.delete(`${DRAFTS_ENDPOINT}${persistedId}/`);
}

/**
 * Clears all drafts for a price list scope on the backend.
 * @param {string} priceListType
 * @param {number|string|null} priceListId
 * @returns {Promise<void>}
 */
export async function clearDraftQueue(priceListType, priceListId) {
    await apiClient.delete(DRAFTS_ENDPOINT, {
        params: draftScopeParams(priceListType, priceListId),
    });
}

/**
 * @returns {string}
 */
function createDraftId() {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
        return crypto.randomUUID();
    }
    return `draft_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

/**
 * @param {Object} payload
 * @param {string[]} existingProductCodes
 * @returns {'create'|'update'}
 */
export function resolveDraftAction(payload, existingProductCodes = []) {
    if (payload?.old_product_code) {
        return 'update';
    }
    const code = String(payload?.product_code || '').trim();
    if (code && existingProductCodes.includes(code)) {
        return 'update';
    }
    return 'create';
}

/**
 * @param {Object} params
 * @param {'create'|'update'} params.action
 * @param {Object} params.payload
 * @param {Object} [params.builderState]
 * @param {string|null} [params.sourceProductCode]
 * @param {number|string|null} [params.previousPrice]
 * @param {'simple'|'builder'} [params.updateMode]
 * @returns {Object}
 */
export function createDraftEntry({
    action,
    payload,
    builderState = null,
    sourceProductCode = null,
    previousPrice = null,
    updateMode = builderState ? 'builder' : 'simple',
}) {
    const normalizedPayload = cloneDeep(payload);
    if (action === 'update' && !normalizedPayload.old_product_code) {
        normalizedPayload.old_product_code = sourceProductCode || normalizedPayload.product_code;
    }

    // Never attach pricing_detail on simple price-only updates.
    if (updateMode === 'simple') {
        delete normalizedPayload.pricing_detail;
        delete normalizedPayload.pricing_settings;
    }

    return {
        id: createDraftId(),
        action,
        updateMode,
        payload: normalizedPayload,
        builderState: builderState ? cloneDeep(builderState) : null,
        sourceProductCode: sourceProductCode || normalizedPayload.old_product_code || null,
        previousPrice,
        createdAt: new Date().toISOString(),
        summary: {
            product_code: normalizedPayload.product_code,
            product_name: normalizedPayload.product_name,
            price: normalizedPayload.price,
            previous_price: previousPrice,
            action,
            update_mode: updateMode,
        },
    };
}

/**
 * @param {Array<Object>} drafts
 * @param {Object} entry
 * @param {string|null} [replaceDraftId]
 * @returns {Array<Object>}
 */
export function upsertDraftEntry(drafts, entry, replaceDraftId = null) {
    const queue = Array.isArray(drafts) ? [...drafts] : [];
    if (replaceDraftId) {
        const index = queue.findIndex((draft) => draft.id === replaceDraftId);
        if (index !== -1) {
            queue[index] = { ...entry, id: replaceDraftId };
            return queue;
        }
    }
    queue.push(entry);
    return queue;
}

/**
 * @param {Array<Object>} drafts
 * @param {string} draftId
 * @returns {Array<Object>}
 */
export function removeDraftEntry(drafts, draftId) {
    return (drafts || []).filter((draft) => draft.id !== draftId);
}

/**
 * @param {Array<Object>} drafts
 * @param {string|null} [excludeDraftId]
 * @returns {string[]}
 */
export function getDraftProductCodes(drafts, excludeDraftId = null) {
    return (drafts || [])
        .filter((draft) => draft.id !== excludeDraftId)
        .map((draft) => String(draft.payload?.product_code || '').trim())
        .filter(Boolean);
}

/**
 * @param {string} productCode
 * @param {Array<Object>} drafts
 * @param {string|null} [excludeDraftId]
 * @returns {Object|null}
 */
export function findDraftByProductCode(drafts, productCode, excludeDraftId = null) {
    const normalized = String(productCode || '').trim();
    if (!normalized) {
        return null;
    }
    return (drafts || []).find((draft) =>
        draft.id !== excludeDraftId
        && String(draft.payload?.product_code || '').trim() === normalized
    ) || null;
}
